import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, List, ListItem, ListItemText, MenuItem, Paper,
  Select, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { Add, Check, Close, Lock, Public, Group } from '@mui/icons-material';
import gitApi from './api';

const visibilityIcon = (v) => v === 'public_open' ? <Public fontSize="small" /> : v === 'public_auth' ? <Group fontSize="small" /> : <Lock fontSize="small" />;

const RepoCard = ({ repo, onOpen }) => (
  <Paper sx={{ p: 2, mb: 1.5, cursor: 'pointer', backgroundColor: '#1d1d1d' }} onClick={() => onOpen(repo)}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {visibilityIcon(repo.visibility)}
      <Typography sx={{ fontWeight: 600 }}>{repo.owner}/{repo.name}</Typography>
      <Chip size="small" label={repo.visibility.replace('_', ' ')} sx={{ ml: 'auto' }} />
    </Box>
    {repo.description && <Typography sx={{ fontSize: 13, opacity: 0.75, mt: 0.5 }}>{repo.description}</Typography>}
  </Paper>
);

const RepoListPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [repos, setRepos] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', visibility: 'private', initReadme: true });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const filterFor = (t) => (t === 0 ? 'explore' : t === 1 ? 'mine' : 'collaborator');

  const load = async () => {
    setLoading(true);
    const res = await gitApi.listRepos(filterFor(tab), query);
    setRepos(res.data?.data || []);
    setLoading(false);
  };

  const loadInvites = async () => {
    const res = await gitApi.getInvitations();
    setInvites(res.data?.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { loadInvites(); }, []);

  const create = async () => {
    setError('');
    if (!form.name.trim()) { setError('Name is required'); return; }
    setCreating(true);
    const res = await gitApi.createRepo(form);
    setCreating(false);
    if (res.status === 200) {
      setDialogOpen(false);
      setForm({ name: '', description: '', visibility: 'private', initReadme: true });
      navigate(`/git/${res.data.data.owner}/${res.data.data.name}`);
    } else {
      setError(res.data?.message || 'Could not create repository');
    }
  };

  const respond = async (id, accept) => {
    await gitApi.respondInvitation(id, accept);
    loadInvites();
  };

  return (
    <Box sx={{ mt: 2, p: 2, backgroundColor: '#151515', borderRadius: 1, minHeight: 'calc(100vh - 110px)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>Repositories</Typography>
        <TextField size="small" placeholder="Search repositories" value={query} onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(); }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>New</Button>
      </Box>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Explore" />
        <Tab label="Your repositories" />
        <Tab label="Collaborating" />
      </Tabs>

      {loading ? <CircularProgress /> : repos.length === 0
        ? <Typography sx={{ opacity: 0.6 }}>No repositories.</Typography>
        : repos.map((repo) => <RepoCard key={`${repo.owner}/${repo.name}`} repo={repo} onOpen={(x) => navigate(`/git/${x.owner}/${x.name}`)} />)}

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>Invitations</Typography>
      {invites.length === 0 ? <Typography sx={{ opacity: 0.6 }}>No pending invitations.</Typography> : (
        <List>
          {invites.map((invite) => (
            <ListItem key={invite.id} sx={{ backgroundColor: '#1d1d1d', borderRadius: 1, mb: 1 }}
              secondaryAction={
                <Box>
                  <Button size="small" startIcon={<Check />} onClick={() => respond(invite.id, true)}>Accept</Button>
                  <Button size="small" color="error" startIcon={<Close />} onClick={() => respond(invite.id, false)}>Decline</Button>
                </Box>
              }>
              <ListItemText primary={`${invite.repoOwner}/${invite.repoName}`} secondary={`Role: ${invite.role} · invited by ${invite.invitedBy}`} />
            </ListItem>
          ))}
        </List>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create a repository</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField label="Name" size="small" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField label="Description" size="small" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <FormControl size="small">
            <InputLabel>Visibility</InputLabel>
            <Select label="Visibility" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}>
              <MenuItem value="private">Private</MenuItem>
              <MenuItem value="public_auth">Public (logged-in only)</MenuItem>
              <MenuItem value="public_open">Public (anyone)</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel control={<Switch checked={form.initReadme} onChange={(e) => setForm({ ...form, initReadme: e.target.checked })} />} label="Initialize with a README" />
          {error && <Typography color="error" sx={{ fontSize: 13 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={creating}>{creating ? <CircularProgress size={18} /> : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepoListPage;
